import React, { useMemo } from 'react';
import { Avatar, Box, Typography, useTheme } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import QuestionAnswerOutlinedIcon from '@mui/icons-material/QuestionAnswerOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import EventNoteOutlinedIcon from '@mui/icons-material/EventNoteOutlined';
import { useTranslation } from 'react-i18next';
import { getInitials } from '../editor/features/roles/utils/rolColorUtils';
import { prepareRoleColorForChat } from '../../utils/roleColorValidation';

function truncate(str, max) {
  if (!str || str.length <= max) return str;
  return str.substring(0, max - 1) + '\u2026';
}

function generateHeuristicCards(role) {
  const cards = [];

  cards.push({
    title: 'Que puedes hacer',
    prompt: 'Que puedes hacer por mi? Explicame tus capacidades.',
  });

  if (role.objectives) {
    const firstSentence = role.objectives.split(/[.!?]/)[0]?.trim();
    if (firstSentence && firstSentence.length > 5) {
      cards.push({
        title: truncate(firstSentence, 40),
        prompt: firstSentence.endsWith('?') ? firstSentence : `${firstSentence}. Ayudame con esto.`,
      });
    }
  }

  if (cards.length < 4) {
    cards.push({
      title: `Empezar con ${truncate(role.name, 20)}`,
      prompt: `Hola ${role.name}, necesito tu ayuda. Por donde empezamos?`,
    });
  }

  if (cards.length < 4) {
    cards.push({
      title: 'Dame un ejemplo',
      prompt: 'Dame un ejemplo concreto de como puedes ayudarme en mi trabajo diario.',
    });
  }

  return cards.slice(0, 4);
}

/**
 * Welcome screen for the chat.
 * Shows agent-specific action cards from DB, heuristic fallback, or generic i18n cards.
 *
 * @param {object} props
 * @param {string} props.logoUrl - Logo URL
 * @param {string} props.brandColor - Brand color
 * @param {string} props.welcomeMessage - Welcome message
 * @param {React.ReactNode} props.children - Additional elements (e.g., chat input)
 * @param {Function} props.onExampleClick - Callback when example prompt is clicked
 * @param {object} [props.role] - Optional role object for agent-specific welcome
 * @param {Array} [props.actionCards] - Optional action cards from DB
 * @param {Function} [props.onAgentClick] - Opens agent dropdown when tapping the identity block
 */
const ChatWelcomeScreen = ({
  logoUrl = '/ai-flock-logo.png',
  brandColor = '#1CB5D4',
  welcomeMessage,
  children,
  onExampleClick,
  role,
  actionCards,
  onAgentClick,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();

  // Icons for generic i18n examples only
  const exampleIcons = [
    <QuestionAnswerOutlinedIcon key="qa" />,
    <DescriptionOutlinedIcon key="desc" />,
    <AccountTreeOutlinedIcon key="tree" />,
    <EventNoteOutlinedIcon key="event" />
  ];

  const resolvedCards = useMemo(() => {
    // Priority 1: DB cards from action_card table
    if (actionCards?.length) {
      return actionCards.map(card => ({ title: card.title, prompt: card.prompt, dynamic: true }));
    }
    // Priority 2: Heuristic from role objectives + tools
    if (role) {
      return generateHeuristicCards(role).map(card => ({ ...card, dynamic: true }));
    }
    // Priority 3: Generic i18n (no role selected)
    const examplesFromI18n = t('welcome.examples', { returnObjects: true });
    return examplesFromI18n.map((example, index) => ({
      ...example,
      icon: exampleIcons[index],
      dynamic: false,
    }));
  }, [actionCards, role, t]);

  const handleExampleClick = (prompt) => {
    if (onExampleClick) {
      onExampleClick(prompt);
    }
  };

  const roleAvatarColors = role
    ? prepareRoleColorForChat(role.color || theme.palette.primary.main, theme)
    : null;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: { xs: 'flex-start', md: 'center' },
        height: '100%',
        width: '100%',
        px: { xs: 2, md: 0 },
        py: { xs: 2, md: 2 },
        gap: { xs: 2, md: 3 },
        overflowY: 'auto',
      }}
    >
      {/* Logo / Agent branding */}
      <Box sx={{ textAlign: 'center' }}>
        {role ? (
          <Box
            onClick={onAgentClick}
            sx={{
              display: 'inline-flex',
              flexDirection: 'column',
              alignItems: 'center',
              ...(onAgentClick && {
                cursor: 'pointer',
                borderRadius: 3,
                px: 2,
                py: 1,
                transition: 'background 0.15s ease',
                '&:hover': { bgcolor: 'action.hover' },
                '&:active': { bgcolor: 'action.selected' },
              }),
            }}
          >
            <Avatar
              sx={{
                width: 64,
                height: 64,
                fontSize: '1.5rem',
                fontWeight: 600,
                background: roleAvatarColors?.avatarBgGradient,
                boxShadow: roleAvatarColors?.avatarShadow,
                color: roleAvatarColors?.avatarText || theme.palette.getContrastText(role.color || theme.palette.primary.main),
                mx: 'auto',
                mb: 1,
              }}
            >
              {getInitials(role.name)}
            </Avatar>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                {role.name}
              </Typography>
              {onAgentClick && (
                <ExpandMoreIcon sx={{ fontSize: 20, color: 'text.disabled', mt: '2px' }} />
              )}
            </Box>
            {role.objectives && (
              <Typography
                variant="body1"
                sx={{
                  color: 'text.secondary',
                  fontSize: { xs: '0.875rem', md: '1rem' },
                  maxWidth: '600px',
                  mx: 'auto',
                }}
              >
                {role.objectives}
              </Typography>
            )}
          </Box>
        ) : (
          <>
            <Box
              component="img"
              src={logoUrl}
              alt="AIFlock"
              sx={{
                width: { xs: '200px', sm: '280px', md: '360px' },
                height: 'auto',
                mb: { xs: 1, md: 2 }
              }}
            />
            <Typography
              variant="body1"
              sx={{
                color: 'text.secondary',
                fontSize: { xs: '0.875rem', md: '1rem' },
                maxWidth: '600px',
                mx: 'auto'
              }}
            >
              {t('welcome.subtitle')}
            </Typography>
          </>
        )}
      </Box>

      {/* Chat input */}
      {children && (
        <Box
          sx={{
            width: '100%',
            maxWidth: { xs: '100%', md: '680px' },
            mx: 'auto',
            flexShrink: 0,
            borderRadius: '16px',
            border: `1px solid ${theme.palette.divider}`,
            overflow: 'hidden',
            boxShadow: theme.palette.mode === 'dark'
              ? '0 2px 8px rgba(0, 0, 0, 0.3)'
              : '0 1px 6px rgba(0, 0, 0, 0.06)',
            transition: 'box-shadow 0.2s ease',
            '&:focus-within': {
              borderColor: theme.palette.mode === 'dark'
                ? theme.palette.primary.dark
                : theme.palette.grey[300],
              boxShadow: theme.palette.mode === 'dark'
                ? '0 2px 12px rgba(0, 0, 0, 0.4)'
                : '0 2px 12px rgba(0, 0, 0, 0.08)',
            }
          }}
        >
          {children}
        </Box>
      )}

      {/* Example prompt cards */}
      {onExampleClick && (
        <Box
          sx={{
            width: '100%',
            maxWidth: { xs: '100%', md: '800px' },
            mx: 'auto',
            mb: { xs: 1, md: 2 }
          }}
        >
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              textAlign: 'center',
              color: 'text.secondary',
              mb: 1.5,
              fontSize: { xs: '0.75rem', md: '0.8rem' }
            }}
          >
            {resolvedCards[0]?.dynamic
              ? t('welcome.suggestedActions', 'Acciones sugeridas:')
              : t('welcome.examplesPrompt')}
          </Typography>

          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: `repeat(${Math.min(resolvedCards.length, 4)}, 1fr)` },
            gap: { xs: 1, md: 1.5 },
          }}>
            {resolvedCards.map((card, index) => (
              <Box
                key={index}
                onClick={() => handleExampleClick(card.prompt)}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.75,
                  p: { xs: 1.5, md: 2 },
                  borderRadius: '12px',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  border: `1px solid ${theme.palette.divider}`,
                  bgcolor: 'transparent',
                  '&:hover': {
                    bgcolor: theme.palette.mode === 'dark'
                      ? 'rgba(255,255,255,0.04)'
                      : 'rgba(0,0,0,0.02)',
                    borderColor: theme.palette.mode === 'dark'
                      ? 'rgba(255,255,255,0.15)'
                      : theme.palette.grey[300],
                  },
                  '&:active': {
                    bgcolor: theme.palette.mode === 'dark'
                      ? 'rgba(255,255,255,0.08)'
                      : 'rgba(0,0,0,0.04)',
                  }
                }}
              >
                {/* Icon only for generic i18n cards */}
                {card.icon && (
                  <Box sx={{
                    color: 'text.secondary',
                    display: 'flex',
                    alignItems: 'center',
                    fontSize: { xs: '1.1rem', md: '1.25rem' },
                  }}>
                    {card.icon}
                  </Box>
                )}
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 600,
                    fontSize: { xs: '0.75rem', md: '0.85rem' },
                    color: 'text.primary',
                    lineHeight: 1.3,
                  }}
                >
                  {card.title}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontSize: { xs: '0.65rem', md: '0.75rem' },
                    lineHeight: 1.4,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {card.prompt}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ChatWelcomeScreen;
